import { useCallback, useEffect } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { useGlobalOrganization } from '@/hooks/useGlobalOrganization';
import { useRealtime } from '@/providers/RealtimeProvider';
import { NOTIFICATIONS_CHANGED_EVENT, openNotificationTarget } from '@/lib/notificationCenter';
import type { AppNotification, AppNotificationPage } from '@/types';

// Not in queryPersister's allowlist on purpose: unread state must never render stale from disk.
const ROOT_KEY = 'notifications';

// A 4xx (e.g. 404 from a backend that predates the notification center) won't fix itself.
const noRetryOn4xx = (failureCount: number, err: unknown) => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status && status >= 400 && status < 500) return false;
  return failureCount < 3;
};

/**
 * Mark every notification query stale, but refetch only the cheap ones that are on screen: the
 * unread count and lists showing a single page (the bell, the first page of the full list).
 * A list scrolled several pages deep would refetch every loaded page sequentially, so it stays as
 * is until the user's next focus/mount.
 */
function refreshNotifications(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: [ROOT_KEY], refetchType: 'none' });
  queryClient.refetchQueries({
    queryKey: [ROOT_KEY],
    type: 'active',
    predicate: (q) => ((q.state.data as { pages?: unknown[] } | undefined)?.pages?.length ?? 0) <= 1,
  });
}

export function useUnreadNotificationCount() {
  const { selectedOrgId } = useGlobalOrganization();
  return useQuery({
    queryKey: [ROOT_KEY, selectedOrgId, 'unread-count'],
    queryFn: () => apiService.getUnreadNotificationCount(),
    enabled: !!selectedOrgId,
    retry: noRetryOn4xx,
  });
}

export function useNotificationList({ unreadOnly = false, pageSize = 20, enabled = true }: { unreadOnly?: boolean; pageSize?: number; enabled?: boolean } = {}) {
  const { selectedOrgId } = useGlobalOrganization();
  return useInfiniteQuery({
    queryKey: [ROOT_KEY, selectedOrgId, 'list', { unreadOnly, pageSize }],
    queryFn: ({ pageParam }) => apiService.getNotifications({ before: pageParam, limit: pageSize, unreadOnly }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_before ?? undefined,
    enabled: enabled && !!selectedOrgId,
    retry: noRetryOn4xx,
  });
}

/**
 * Read-state changes patch the cached lists in place (no refetch of loaded pages), then refresh the
 * unread count. An item marked read stays visible in an "Unread" list until it next reloads.
 */
export function useNotificationActions() {
  const queryClient = useQueryClient();
  const { selectedOrgId } = useGlobalOrganization();

  const patchLists = (update: (n: AppNotification) => AppNotification) =>
    queryClient.setQueriesData<InfiniteData<AppNotificationPage>>(
      { queryKey: [ROOT_KEY, selectedOrgId, 'list'] },
      (data) => data && { ...data, pages: data.pages.map(p => ({ ...p, items: p.items.map(update) })) },
    );

  const handlers = {
    onSettled: () => refreshNotifications(queryClient),
    // Rare: drop the optimistic patch by reloading everything.
    onError: () => queryClient.invalidateQueries({ queryKey: [ROOT_KEY] }),
  };
  const now = () => new Date().toISOString();

  const markRead = useMutation({
    mutationFn: (id: string) => apiService.markNotificationRead(id),
    onMutate: (id) => patchLists(n => (n.id === id && !n.read_at ? { ...n, read_at: now() } : n)),
    ...handlers,
  });
  const markUnread = useMutation({
    mutationFn: (id: string) => apiService.markNotificationUnread(id),
    onMutate: (id) => patchLists(n => (n.id === id ? { ...n, read_at: null } : n)),
    ...handlers,
  });
  const markAllRead = useMutation({
    mutationFn: () => apiService.markAllNotificationsRead(),
    onMutate: () => patchLists(n => (n.read_at ? n : { ...n, read_at: now() })),
    ...handlers,
  });

  return { markRead, markUnread, markAllRead };
}

/** Mark read (if unread) and open what the notification points at. */
export function useOpenNotification() {
  const { markRead } = useNotificationActions();
  const { mutate } = markRead;
  return useCallback((n: AppNotification) => {
    if (!n.read_at) mutate(n.id);
    openNotificationTarget(n.type, n.payload);
  }, [mutate]);
}

/**
 * Keep notification queries fresh: a NotificationCreated SignalR event, a (re)connect (events may
 * have been missed while disconnected) and push-tap read marks all refresh. Mount once (the bell).
 */
export function useNotificationLiveUpdates() {
  const { connection, status } = useRealtime();
  const queryClient = useQueryClient();

  useEffect(() => {
    const refresh = () => refreshNotifications(queryClient);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);
    if (!connection) return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);

    connection.on('NotificationCreated', refresh);
    return () => {
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);
      connection.off('NotificationCreated', refresh);
    };
  }, [connection, queryClient]);

  useEffect(() => {
    if (status === 'connected') refreshNotifications(queryClient);
  }, [status, queryClient]);
}
